import { Seo } from "@/components/seo/Seo";
import { staticSeoRoutes } from "@/config/seo.config";

const meta = staticSeoRoutes.find((route) => route.path === "/privacy")!;

export function PrivacyPage() {
  return (
    <>
      <Seo
        path={meta.path}
        title={meta.title}
        description={meta.description}
        robots={meta.robots}
      />
      <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <h1 className="text-4xl font-semibold">Privacy Policy</h1>
        <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
          Last updated: June 2026
        </p>

        <div className="mt-8 space-y-8 leading-7 text-neutral-700 dark:text-neutral-300">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              1. Overview
            </h2>
            <p>
              VideoTranscriptSaver ("we", "our", or "the Service") is a
              browser-based transcription utility. This Privacy Policy explains
              what information is collected when you use the Service, how it is
              used, and your rights with respect to that information. By using
              the Service you agree to the practices described here.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              2. Information We Do Not Collect
            </h2>
            <p>
              We do not collect, store, or retain the raw media files, audio
              recordings, or video files you provide to the Service. We do not
              store the transcripts, captions, summaries, notes, action items,
              or chapters generated for you. We do not store media URLs or
              prompts you submit. Your content is processed transiently and is
              not persisted on our infrastructure beyond the duration required
              to fulfil your request.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              3. Local Browser Storage
            </h2>
            <p>
              To preserve your work across page refreshes and navigation, the
              tool saves in-progress state — including partial transcripts and
              notes — in your browser's local storage. This data never leaves
              your device except as part of an active processing request. You
              can clear this state at any time using the tool's reset control,
              or by clearing your browser's site data. Locally stored tool state
              expires automatically after 180 days of inactivity unless you
              clear it sooner.
            </p>
            <p>
              We also use local storage to remember your interface preferences
              (such as colour scheme) and your cookie consent decision.
              Non-essential storage (such as advertising-related scripts) is
              activated only after you give explicit consent, and that consent
              preference is stored locally for up to 180 days.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              4. Operational Metadata
            </h2>
            <p>
              To maintain service reliability, detect abuse, and enforce
              fair-use limits, we record a minimal set of anonymised operational
              signals for each request. This data does not identify you
              personally and includes only general information about the nature
              and outcome of the request (such as whether it succeeded, its
              approximate size category, and an anonymised session indicator
              derived from your network context). No raw content is stored as
              part of this metadata.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              5. Third-Party Processing
            </h2>
            <p>
              Media submitted to the Service is processed using reputable
              third-party AI infrastructure providers. These providers receive
              only the data necessary to fulfil your request (the media or text
              to be processed) and are bound by their own data processing and
              security agreements. We do not sell, rent, or share your content
              with third parties for marketing or advertising purposes.
              Third-party provider credentials and configuration are never
              exposed to the browser or to end users.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              6. Waitlist Registration
            </h2>
            <p>
              If you voluntarily submit your name and email address to join a
              future premium-tier waitlist, we store that information along with
              basic submission metadata (such as the date of submission and the
              page from which you subscribed) for the sole purpose of managing
              and communicating about that waitlist. You may request deletion of
              your waitlist entry at any time by contacting us. We will never
              use your waitlist email for unrelated marketing without your
              consent.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              7. Cookies and Advertising
            </h2>
            <p>
              The Service may display third-party advertisements. Advertising
              partners may use cookies or similar technologies to serve ads
              relevant to your interests, subject to your consent. We do not
              control the data practices of advertising partners. You can
              withdraw consent or manage cookie preferences at any time through
              the consent control accessible on the site.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              8. Children's Privacy
            </h2>
            <p>
              The Service is not directed at children under the age of 13. We do
              not knowingly collect personal information from children under 13.
              If you believe a child has submitted personal information to us,
              please contact us so we can delete it.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              9. Data Security
            </h2>
            <p>
              We implement industry-standard technical and organisational
              measures to protect information processed through the Service.
              However, no method of transmission over the internet or electronic
              storage is completely secure. We cannot guarantee absolute
              security and disclaim liability for breaches that occur despite
              reasonable precautions.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              10. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. The "Last
              updated" date at the top of this page reflects the most recent
              revision. Continued use of the Service after changes are posted
              constitutes acceptance of the updated policy. We encourage you to
              review this page periodically.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              11. Contact
            </h2>
            <p>
              If you have questions or requests regarding this Privacy Policy,
              please contact us via the details provided on our website. We will
              respond to legitimate privacy inquiries within a reasonable
              timeframe.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
